import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Award,
  Calculator,
  Check,
  CheckCircle2,
  Crown,
  GraduationCap,
  Loader2,
  MapPin,
  MessageCircle,
  Phone,
  Share2,
  ShieldCheck,
  Sparkles,
  TrendingDown,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { API } from "@/lib/api";

const COUNTRY_FLAG = {
  Russia: "🇷🇺",
  Bulgaria: "🇧🇬",
  Moldova: "🇲🇩",
  Uzbekistan: "🇺🇿",
  Georgia: "🇬🇪",
  Kazakhstan: "🇰🇿",
  Kyrgyzstan: "🇰🇬",
  Belarus: "🇧🇾",
  Kazakhstan2: "🇰🇿",
};

function formatINR(num) {
  if (num == null) return "—";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(num);
}

function lakhs(num) {
  if (num == null) return "—";
  if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
  return `₹${formatINR(num)}`;
}

export default function BudgetCalculatorPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const tenantSlug = slug || null;

  const [step, setStep] = useState(1); // 1=form, 2=picker, 3=results
  const [form, setForm] = useState({
    name: "",
    phone: "",
    whatsapp_number: "",
    waSameAsPhone: true,
    email: "",
    neet_score: "",
    preferred_country: "",
  });
  const [countries, setCountries] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [picked, setPicked] = useState([]); // ids (max 3)
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [tenantName, setTenantName] = useState("MedAdmits");

  // Capture UTM
  const utm = useMemo(
    () => ({
      utm_source: searchParams.get("utm_source") || undefined,
      utm_medium: searchParams.get("utm_medium") || undefined,
      utm_campaign: searchParams.get("utm_campaign") || undefined,
      referrer: document.referrer || undefined,
    }),
    [searchParams]
  );

  // Load countries on mount
  useEffect(() => {
    const q = tenantSlug ? `?tenant_slug=${tenantSlug}` : "";
    fetch(`${API}/budget/countries${q}`)
      .then((r) => r.json())
      .then((d) => setCountries(d.countries || []))
      .catch(() => {});
  }, [tenantSlug]);

  // Load universities when country changes (on step 2)
  useEffect(() => {
    if (step !== 2 || !form.preferred_country) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (tenantSlug) params.set("tenant_slug", tenantSlug);
        params.set("country", form.preferred_country);
        const res = await fetch(`${API}/budget/universities?${params}`);
        const d = await res.json();
        if (!cancelled) setUniversities(d.universities || []);
      } catch {
        if (!cancelled) setUniversities([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [step, form.preferred_country, tenantSlug]);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function gotoStep2(e) {
    e?.preventDefault?.();
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error("Please enter your name");
      return;
    }
    if (!/^\d{10,15}$/.test(form.phone.replace(/\D/g, ""))) {
      toast.error("Enter a valid 10-digit phone number");
      return;
    }
    const ns = parseInt(form.neet_score || "0", 10);
    if (isNaN(ns) || ns < 0 || ns > 720) {
      toast.error("NEET score must be between 0 and 720");
      return;
    }
    if (!form.preferred_country) {
      toast.error("Pick a preferred country");
      return;
    }
    setPicked([]);
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function togglePick(id) {
    setPicked((prev) => {
      if (prev.includes(id)) return prev.filter((p) => p !== id);
      if (prev.length >= 3) {
        toast.error("You can select up to 3 universities. Deselect one first.");
        return prev;
      }
      return [...prev, id];
    });
  }

  async function submitCalculate() {
    if (picked.length === 0) {
      toast.error("Pick at least one university");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        whatsapp_number: form.waSameAsPhone
          ? form.phone.trim()
          : form.whatsapp_number.trim() || form.phone.trim(),
        email: form.email.trim() || undefined,
        neet_score: parseInt(form.neet_score, 10),
        preferred_country: form.preferred_country,
        university_ids: picked,
        tenant_slug: tenantSlug || undefined,
        ...utm,
      };
      const res = await fetch(`${API}/budget/calculate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Calculation failed");
      }
      const data = await res.json();
      setResult(data);
      setTenantName(data.tenant_name || "MedAdmits");
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.message || "Could not generate budget");
    } finally {
      setLoading(false);
    }
  }

  function shareOnWhatsApp() {
    if (!result) return;
    const lines = [
      `🎓 MBBS Abroad Budget Estimate for ${result.student_name}`,
      `NEET score: ${result.neet_score}`,
      "",
      ...result.breakdowns.map(
        (b) =>
          `• ${b.name} (${b.city}, ${b.country}) — 6yr total: ₹${formatINR(b.total_inr)}`
      ),
      "",
      `Powered by ${result.tenant_name}.`,
      "",
      "Disclaimer: Universities may revise fees at their discretion. Final fees confirmed at admission.",
    ];
    const text = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/?text=${text}`, "_blank");
  }

  // ---------- RENDER ----------
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAF9F6] to-white text-[#0A2540]" data-testid="budget-calculator-page">
      {/* Header */}
      <header className="border-b border-[#0A2540]/8 bg-white/70 backdrop-blur sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-[#0A2540] flex items-center justify-center text-white">
              <GraduationCap className="h-5 w-5" />
            </div>
            <div>
              <div className="font-semibold tracking-tight">MedAdmits</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#0A2540]/60">
                MBBS Budget Calculator
              </div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-3 text-xs text-[#0A2540]/70">
            <span className="flex items-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> NMC-approved unis only
            </span>
            <span>·</span>
            <span>100% Free</span>
          </div>
        </div>
        {/* Progress strip */}
        <div className="mx-auto max-w-6xl px-5 pb-3">
          <div className="flex items-center gap-2 text-xs">
            {[
              { n: 1, label: "Your details" },
              { n: 2, label: "Pick universities" },
              { n: 3, label: "Your budget" },
            ].map((s, i) => (
              <div key={s.n} className="flex items-center gap-2">
                <div
                  className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                    step >= s.n ? "bg-[#C9A961] text-[#0A2540]" : "bg-[#0A2540]/10 text-[#0A2540]/60"
                  }`}
                >
                  {step > s.n ? <Check className="h-3 w-3" /> : s.n}
                </div>
                <span className={step >= s.n ? "text-[#0A2540] font-medium" : "text-[#0A2540]/50"}>
                  {s.label}
                </span>
                {i < 2 && <span className="h-px w-6 bg-[#0A2540]/15" />}
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* STEP 1 — Hero + Form */}
      {step === 1 && (
        <section className="mx-auto max-w-6xl px-5 py-6 md:py-12 md:grid md:grid-cols-2 md:gap-10 md:items-start">
          {/* Hero title (always first) */}
          <div data-testid="budget-hero" className="md:order-1">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight">
              Calculate Your MBBS Abroad Budget in Just 30 Seconds
            </h1>
            <p className="mt-4 md:mt-5 text-sm md:text-base text-[#0A2540]/75 leading-relaxed max-w-xl">
              Get an instant personalised estimate of tuition fees, hostel expenses, visa costs, and total MBBS budget for top medical universities abroad.
            </p>
            {/* Bullets + Trust bar — DESKTOP only (mobile shows them below the form) */}
            <div className="hidden md:block">
              <ul className="mt-6 space-y-2.5">
                {[
                  "Based on Your NEET Score",
                  "Compare Multiple Countries",
                  "Discover Budget-Friendly Universities",
                  "100% Free Report",
                ].map((p) => (
                  <li key={p} className="flex items-center gap-3 text-sm">
                    <CheckCircle2 className="h-4.5 w-4.5 text-emerald-600" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 rounded-2xl border border-[#0A2540]/10 bg-white px-5 py-4">
                <div className="text-xs uppercase tracking-[0.18em] text-[#0A2540]/60 mb-2">
                  Trusted by 5,000+ Medical Aspirants
                </div>
                <div className="grid grid-cols-2 gap-2 text-[12px] text-[#0A2540]/75">
                  <span>⭐ 100+ Medical Universities</span>
                  <span>⭐ 15+ Countries</span>
                  <span>⭐ Expert MBBS Guidance</span>
                  <span>⭐ Transparent Planning</span>
                  <span className="col-span-2">⭐ No Hidden Charges</span>
                </div>
              </div>
            </div>
          </div>

          {/* Form (mobile: directly under hero title; desktop: right column, sticky) */}
          <form
            onSubmit={gotoStep2}
            className="md:order-2 mt-5 md:mt-0 bg-white rounded-2xl border border-[#0A2540]/10 shadow-[0_20px_60px_-30px_rgba(10,37,64,0.35)] p-5 md:p-7 space-y-4 md:sticky md:top-28"
            data-testid="budget-form"
          >
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#C9A961] font-semibold mb-1">
                Step 1 of 3
              </div>
              <h2 className="text-xl font-bold">Tell us about yourself</h2>
              <p className="text-xs text-[#0A2540]/60 mt-1">
                30 seconds · No spam · 100% Free
              </p>
            </div>

            <FormField label="Your Name" icon={User}>
              <Input
                data-testid="bc-name"
                placeholder="Aman Sharma"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
              />
            </FormField>

            <FormField label="Phone (WhatsApp)" icon={Phone}>
              <Input
                data-testid="bc-phone"
                type="tel"
                placeholder="98765 43210"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
              />
            </FormField>

            <FormField label="NEET Score (expected or actual)" icon={Award}>
              <Input
                data-testid="bc-neet"
                type="number"
                min={0}
                max={720}
                placeholder="e.g. 580"
                value={form.neet_score}
                onChange={(e) => update("neet_score", e.target.value)}
              />
            </FormField>

            <FormField label="Preferred Country" icon={MapPin}>
              <Select
                value={form.preferred_country}
                onValueChange={(v) => update("preferred_country", v)}
              >
                <SelectTrigger data-testid="bc-country">
                  <SelectValue placeholder="Pick a country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((c) => (
                    <SelectItem key={c} value={c}>
                      {COUNTRY_FLAG[c] || "🎓"} {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            <Button
              type="submit"
              data-testid="bc-step1-cta"
              className="w-full bg-[#0A2540] hover:bg-[#0A2540]/90 text-white py-6 text-base font-semibold rounded-xl"
            >
              <Calculator className="h-4.5 w-4.5 mr-2" />
              Calculate My MBBS Budget
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>

            <p className="text-[10.5px] text-[#0A2540]/55 leading-relaxed">
              By continuing, you agree to receive a budget report on WhatsApp. We never share your data with third parties.
            </p>
          </form>

          {/* Mobile-only: bullets + trust shown BELOW the form */}
          <div className="md:hidden mt-6 space-y-5">
            <ul className="space-y-2.5">
              {[
                "Based on Your NEET Score",
                "Compare Multiple Countries",
                "Discover Budget-Friendly Universities",
                "100% Free Report",
              ].map((p) => (
                <li key={p} className="flex items-center gap-3 text-sm">
                  <CheckCircle2 className="h-4.5 w-4.5 text-emerald-600" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <div className="rounded-2xl border border-[#0A2540]/10 bg-white px-5 py-4">
              <div className="text-xs uppercase tracking-[0.18em] text-[#0A2540]/60 mb-2">
                Trusted by 5,000+ Medical Aspirants
              </div>
              <div className="grid grid-cols-2 gap-2 text-[12px] text-[#0A2540]/75">
                <span>⭐ 100+ Medical Universities</span>
                <span>⭐ 15+ Countries</span>
                <span>⭐ Expert MBBS Guidance</span>
                <span>⭐ Transparent Planning</span>
                <span className="col-span-2">⭐ No Hidden Charges</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* STEP 2 — University Picker */}
      {step === 2 && (
        <section className="mx-auto max-w-6xl px-5 py-8" data-testid="budget-picker">
          <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
            <div>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex items-center gap-1 text-xs text-[#0A2540]/70 hover:text-[#0A2540] mb-2"
                data-testid="bc-back-to-step1"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
              <h2 className="text-2xl sm:text-3xl font-bold">
                {COUNTRY_FLAG[form.preferred_country] || "🎓"} Pick up to 3 universities in {form.preferred_country}
              </h2>
              <p className="text-sm text-[#0A2540]/70 mt-1">
                We&apos;ll show you a side-by-side 6-year breakdown in INR. Compare and decide with confidence.
              </p>
            </div>
            <div className="rounded-xl bg-[#0A2540] text-white px-4 py-3 text-sm font-medium" data-testid="bc-pick-count">
              {picked.length} of 3 selected
            </div>
          </div>

          {loading ? (
            <div className="text-center py-20 text-[#0A2540]/60">
              <Loader2 className="h-6 w-6 animate-spin mx-auto" />
              <p className="mt-3 text-sm">Loading universities…</p>
            </div>
          ) : universities.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-[#0A2540]/15">
              <p className="text-[#0A2540]/60">
                No universities listed yet for {form.preferred_country}. Try another country.
              </p>
              <Button variant="outline" className="mt-4" onClick={() => setStep(1)}>
                Pick another country
              </Button>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {universities.map((u) => {
                  const checked = picked.includes(u.id);
                  return (
                    <button
                      type="button"
                      key={u.id}
                      onClick={() => togglePick(u.id)}
                      data-testid={`bc-uni-${u.id}`}
                      className={`text-left rounded-2xl border-2 bg-white overflow-hidden transition-all ${
                        checked
                          ? "border-[#C9A961] shadow-[0_18px_48px_-22px_rgba(201,169,97,0.55)] scale-[1.01]"
                          : "border-[#0A2540]/10 hover:border-[#0A2540]/30"
                      }`}
                    >
                      <div className="aspect-[16/9] bg-gradient-to-br from-[#0A2540] to-[#0A2540]/70 relative overflow-hidden">
                        <UniImage
                          src={u.image_url}
                          name={u.name}
                          country={u.country}
                        />
                        {checked && (
                          <div className="absolute top-3 right-3 h-7 w-7 rounded-full bg-[#C9A961] flex items-center justify-center">
                            <Check className="h-4 w-4 text-[#0A2540]" />
                          </div>
                        )}
                        {u.nmc_approved && (
                          <div className="absolute bottom-3 left-3 rounded-full bg-emerald-600/95 px-2.5 py-1 text-[10px] font-semibold text-white uppercase tracking-wide flex items-center gap-1">
                            <ShieldCheck className="h-3 w-3" /> NMC Approved
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-[#0A2540]/55">
                          {u.city}, {u.country}
                        </div>
                        <div className="font-bold text-[15px] mt-1 leading-tight">
                          {u.name}
                        </div>
                        {u.notes && (
                          <p className="text-[12px] text-[#0A2540]/65 mt-2 line-clamp-2">
                            {u.notes}
                          </p>
                        )}
                        <div className="mt-3 pt-3 border-t border-[#0A2540]/8 flex items-center justify-between">
                          <span className="text-[11px] text-[#0A2540]/60">
                            {u.duration_years || 6}-year course
                          </span>
                          <span className="text-[11px] font-semibold text-[#0A2540]">
                            {u.currency}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="sticky bottom-0 left-0 right-0 mt-8 -mx-5 px-5 py-4 bg-white/95 backdrop-blur border-t border-[#0A2540]/10">
                <div className="mx-auto max-w-6xl flex items-center justify-between gap-3">
                  <div className="text-sm">
                    <span className="font-semibold">{picked.length}</span> of 3 selected
                    {picked.length === 0 && (
                      <span className="text-[#0A2540]/60"> · Pick at least one</span>
                    )}
                  </div>
                  <Button
                    onClick={submitCalculate}
                    disabled={picked.length === 0 || loading}
                    data-testid="bc-reveal-budget"
                    className="bg-[#C9A961] hover:bg-[#b89651] text-[#0A2540] font-semibold px-6 py-5 rounded-xl"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-2" />
                    )}
                    Reveal my budget
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </section>
      )}

      {/* STEP 3 — Results */}
      {step === 3 && result && (
        <section className="mx-auto max-w-6xl px-5 py-8" data-testid="budget-results">
          {/* Personalized headline */}
          <div className="mb-8">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#C9A961] font-semibold">
              Your Personalised Budget Report
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mt-2">
              Hello {result.student_name.split(" ")[0]},
            </h2>
            <p className="text-sm text-[#0A2540]/75 mt-1 max-w-2xl">
              Based on your NEET score of <b>{result.neet_score}</b> and your selected universities in <b>{form.preferred_country}</b>, here are the most suitable options for you.
            </p>
          </div>

          {/* Scholarship hint (combined-total card removed per UX request) */}
          {result.scholarship_hint ? (
            <div className="mb-8 rounded-2xl bg-[#C9A961]/15 border border-[#C9A961]/30 p-5 md:p-6" data-testid="bc-scholarship-hint">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0A2540]/70 font-semibold">
                <Crown className="h-3.5 w-3.5 text-[#C9A961]" /> Scholarship Hint
              </div>
              <div className="text-2xl md:text-3xl font-bold mt-2">
                You may qualify for up to {result.scholarship_hint.percentage}% off
              </div>
              <div className="text-sm text-[#0A2540]/75 mt-1">
                Approx ₹{formatINR(result.scholarship_hint.amount)} saving · <b>{result.scholarship_hint.tier}</b> tier
              </div>
              <p className="text-xs text-[#0A2540]/65 mt-3 leading-relaxed">
                Talk to our counsellor to verify eligibility and lock in your offer.
              </p>
            </div>
          ) : (
            <div className="mb-8 rounded-2xl bg-slate-50 border border-slate-200 p-5">
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                Talk to a counsellor
              </div>
              <div className="text-base font-semibold mt-2">
                Free 15-min strategy call
              </div>
              <p className="text-[11px] text-slate-600 mt-2">
                Score-based scholarships available for NEET 400+.
              </p>
            </div>
          )}

          {/* Per-university breakdown */}
          <div className="space-y-6">
            {result.breakdowns.map((b) => {
              const isCheapest = b.university_id === result.cheapest_id && result.breakdowns.length > 1;
              return (
                <div
                  key={b.university_id}
                  className={`rounded-2xl bg-white border-2 overflow-hidden ${
                    isCheapest ? "border-[#C9A961]" : "border-[#0A2540]/10"
                  }`}
                  data-testid={`bc-result-${b.university_id}`}
                >
                  <div className="p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-[#0A2540]/8 bg-[#FAF9F6]">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.18em] text-[#0A2540]/60">
                        {b.city}, {b.country}
                      </div>
                      <div className="text-lg sm:text-xl font-bold mt-0.5 flex items-center gap-2">
                        {b.name}
                        {isCheapest && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#C9A961] text-[#0A2540] text-[10px] font-bold uppercase tracking-wide px-2 py-0.5">
                            <TrendingDown className="h-3 w-3" /> Most Affordable
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] uppercase tracking-[0.18em] text-[#0A2540]/55">
                        6-year total
                      </div>
                      <div className="text-2xl font-bold text-[#0A2540]">
                        ₹{formatINR(b.total_inr)}
                      </div>
                      <div className="text-[11px] text-[#0A2540]/55">
                        {b.currency} {formatINR(b.total_native)} @ 1{b.currency}=₹{b.fx_rate_to_inr}
                      </div>
                    </div>
                  </div>

                  {/* Year table */}
                  <div className="p-5 sm:p-6 overflow-x-auto">
                    <table className="w-full text-sm min-w-[480px]">
                      <thead>
                        <tr className="text-[10.5px] uppercase tracking-[0.18em] text-[#0A2540]/55 border-b border-[#0A2540]/10">
                          <th className="text-left py-2 pr-3">Year</th>
                          <th className="text-right py-2 px-3">Tuition ({b.currency})</th>
                          <th className="text-right py-2 px-3">Hostel ({b.currency})</th>
                          <th className="text-right py-2 pl-3">Total in INR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {b.rows.map((r) => (
                          <tr key={r.year} className="border-b border-[#0A2540]/5">
                            <td className="py-2 pr-3 font-semibold">Year {r.year}</td>
                            <td className="py-2 px-3 text-right">{formatINR(r.tuition_native)}</td>
                            <td className="py-2 px-3 text-right">{formatINR(r.hostel_native)}</td>
                            <td className="py-2 pl-3 text-right font-bold text-[#0A2540]">
                              ₹{formatINR(r.total_inr)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Disclaimer */}
          <div className="mt-6 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-[12px] text-amber-900 leading-relaxed">
            <b>Please note:</b> {result.disclaimer}
          </div>

          {/* CTAs */}
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            <Button
              onClick={shareOnWhatsApp}
              data-testid="bc-share-parents"
              className="bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-base font-semibold rounded-xl"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share with Parents on WhatsApp
            </Button>
            <Button
              onClick={() => {
                window.location.href = "/register";
              }}
              data-testid="bc-take-test"
              className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white py-6 text-base font-semibold rounded-xl"
            >
              <Award className="h-4 w-4 mr-2" />
              Take Scholarship Test (Earn up to ₹4L off)
            </Button>
          </div>
          <div className="mt-3 text-center">
            <button
              type="button"
              onClick={() => {
                setStep(1);
                setResult(null);
                setPicked([]);
              }}
              className="text-xs text-[#0A2540]/60 hover:text-[#0A2540] underline"
              data-testid="bc-restart"
            >
              Start over with different universities
            </button>
          </div>
        </section>
      )}

      <footer className="mt-12 border-t border-[#0A2540]/8 py-6 text-center text-[11px] text-[#0A2540]/55">
        © {new Date().getFullYear()} {tenantName} · MBBS Budget Calculator
      </footer>
    </div>
  );
}

function FormField({ label, icon: Icon, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.18em] text-[#0A2540]/65 flex items-center gap-1.5 mb-1.5">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </Label>
      {children}
    </div>
  );
}

function UniImage({ src, name, country }) {
  const [errored, setErrored] = useState(!src);
  if (errored) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center text-center px-4 text-white">
        <GraduationCap className="h-8 w-8 opacity-70 mb-1.5" />
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/65">
          {COUNTRY_FLAG[country] || ""} {country}
        </div>
        <div className="text-sm font-semibold mt-1 leading-tight line-clamp-2">
          {name}
        </div>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={name}
      onError={() => setErrored(true)}
      loading="lazy"
      className="h-full w-full object-cover"
    />
  );
}
